import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import { applicationDocumentsStore } from 'pages/ApplicationDetailsPage/model/applicationDocumentsStore'
import s from './styles/AssignActivityModal.module.scss'
import Button from 'shared/ui/Button/Button'
import Toast from 'shared/ui/Toast/Toast'
import { ReactComponent as TitleIcon } from 'assets/icons/tablet_dock.svg'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_very_long_right.svg'
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
import { ReactComponent as ArrowShortIcon } from 'assets/icons/arrowLeft.svg'
import { ReactComponent as SendIcon } from 'assets/icons/send_icon_2.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg'

import StepTypeActivity from './steps/StepTypeActivity/StepTypeActivity'
import StepTypeApplications from './steps/StepTypeApplications/StepTypeApplications'
import StepTimeline from './steps/StepTimeline/StepTimeline'
import StepDescription from './steps/StepDescription/StepDescription'
import StepRecipients from './steps/StepRecipients/StepRecipients'
import StepPreview from './steps/StepPreview/StepPreview'

import { useAssignActivityModal } from './hooks/useAssignActivityModal'

const AssignActivityModal = observer(
	({
		isOpen,
		onClose,
		// From parent (e.g. store on Students page) — passed to hook as initial selection
		selectedStudents: selectedStudentsFromStore = [],
		savedLists = [],
		// Initial grade filter from parent; hook syncs it to its own localActiveGrade state
		activeGrade: initialActiveGrade = 'all',
		onSelectList,
		onSetGrade,
		onSuccess,
		clearSelectionOnOpen = true,
		editMode = false,
		assignment = null,
		tepeModal = '',
		initialAssignmentType = '',
		missingItems = [],
		uploadedItems = [],
		applicationDocumentPreset = null,
		applicationModalInstanceKey = 0,
		initialUniversityId = '',
		initialUniversityName = '',
		initialUniversityLogoUrl = '',
		initialApplicationTargetId = '',
		initialEssayMode = '',
		initialTaskTitle = '',
		initialStepId = '',
		lockRecipients = false,
	}) => {
		const hasPresetMissing = Array.isArray(
			applicationDocumentPreset?.missingItems,
		)
		const hasPresetUploaded = Array.isArray(
			applicationDocumentPreset?.uploadedItems,
		)
		const hasLegacyPresetItems = applicationDocumentPreset?.items?.length > 0

		useEffect(() => {
			if (!isOpen || tepeModal !== 'applications') return
			const student = selectedStudentsFromStore?.[0] || {}
			const studentId = String(
				student.id || student.studentId || student.student_id || '',
			).trim()
			if (!studentId) return
			void applicationDocumentsStore.load(studentId, { force: true })
		}, [
			isOpen,
			tepeModal,
			applicationModalInstanceKey,
			selectedStudentsFromStore,
		])

		const stepMissing = hasPresetMissing
			? applicationDocumentPreset.missingItems
			: hasLegacyPresetItems
				? applicationDocumentPreset.items
				: missingItems.length > 0
					? missingItems
					: applicationDocumentsStore.missingItemsForAssign
		const stepUploaded = hasPresetUploaded
			? applicationDocumentPreset.uploadedItems
			: hasLegacyPresetItems
				? []
				: uploadedItems.length > 0
					? uploadedItems
					: applicationDocumentsStore.uploadedItemsForAssign
		const stepLocked = Boolean(applicationDocumentPreset?.locked)

		const documentsLoading =
			tepeModal === 'applications' &&
			!hasPresetMissing &&
			!hasLegacyPresetItems &&
			applicationDocumentsStore.isLoading &&
			missingItems.length === 0

		const {
			steps,
			activeStep,
			stepIndex,

			activityType,
			selectActivityType,

			selectedStudentsLocal,
			setIsTimelineValid,
			timelineData,
			setTimelineDataHandler,

			description,
			setDescription,
			customMessage,
			setCustomMessage,

			preview,
			setPreviewField,
			togglePreviewFlag,
			selectedActivityTitle,

			// TemplateCard props for review step
			activityText,
			setActivityText,
			isEditing,
			startEdit,
			acceptEdit,
			isSaved,
			askAiText,
			setAskAiText,
			onAskAi,

			// Footer state & handlers
			isFirstStep,
			isLastStep,
			isPrimaryButtonDisabled,
			isSubmitting,
			handleBackClick,
			handleNextClick,
			goToStep,
			isStepAccessible,
			isStepLocked,
			completedSteps,
			totalSteps,
			currentStepNumber,

			close,

			showToast,
			closeToast,
			toastMessage,

			listLabel,
			modalTitle,
			buttonText,
			localActiveGrade,
			handleSelectList,
			savedLists: savedListsFromHook,
			isMobile,

			onDocumentTargetsChange,
			essayPrompt,
			setEssayPrompt,
			essayWordLimit,
			setEssayWordLimit,
			essayMode,
			setEssayMode,

			lorRecommenderName,
			setLorRecommenderName,
			lorProfessorDesignation,
			setLorProfessorDesignation,
			essayUniversityOptions,
			selectedEssayUniversityTargetId,
			setSelectedEssayUniversityTargetId,
			isEssayUniversityLoading,
		} = useAssignActivityModal({
			isOpen,
			onClose,
			selectedStudents: selectedStudentsFromStore,
			savedLists,
			activeGrade: initialActiveGrade,
			onSelectList,
			onSetGrade,
			onSuccess,
			clearSelectionOnOpen,
			editMode,
			assignment,
			tepeModal,
			initialAssignmentType,
			initialUniversityId,
			initialUniversityName,
			initialUniversityLogoUrl,
			initialApplicationTargetId,
			initialEssayMode,
			initialTaskTitle,
			initialStepId,
			lockRecipients,
		})

		const content = isOpen ? (
			<div
				className={s.overlay}
				role="dialog"
				aria-modal="true"
				aria-label="Assign Activity"
			>
				<div className={s.backdrop} onClick={close} />

				<div className={s.modal} onClick={e => e.stopPropagation()}>
					<header className={s.header}>
						<div className={s.header__inner}>
							<div className={s.header__title}>
								<span className={s.header__icon}>
									<TitleIcon />
								</span>
								<span className={s.header__text}>{modalTitle}</span>
							</div>
							{!isMobile && (
								<div className={s.header__arrow}>
									<ArrowIcon />
								</div>
							)}
							<div className={s.savedList}>
								<div className={s.savedList__icon}>
									<PeopleIcon />
								</div>
								<span className={s.savedList__text}>
									{tepeModal === 'applications' || lockRecipients
										? selectedStudentsLocal?.[0]?.name || 'Student'
										: listLabel}
								</span>
							</div>
						</div>

						<button
							type="button"
							className={s.closeButton}
							onClick={close}
							aria-label="Close"
						>
							✕
						</button>
					</header>

					<div className={s.layout}>
						<aside className={s.sidebar}>
							<ul className={s.steps}>
								{steps.map((step, index) => {
									const isActive = index === stepIndex
									const isDone =
										!isActive &&
										(index < stepIndex || completedSteps.has(step.id))
									const isAccessible = isStepAccessible(index)
									const canClick = isAccessible && !isActive
									const isLocked = isStepLocked(step.id)

									return (
										<li
											key={step.id}
											className={[
												s.step,
												isActive ? s.active : '',
												isDone ? s.done : '',
												!isAccessible ? s.disabled : '',
												isLocked ? s.locked : '',
											]
												.filter(Boolean)
												.join(' ')}
											onClick={() => goToStep(index)}
											style={{
												cursor: !isLocked && canClick ? 'pointer' : 'default',
											}}
										>
											<div className={s.step__bullet}>
												{isLocked ? (
													<LockIcon />
												) : isDone && !isActive ? (
													<CheckIcon />
												) : step.icon ? (
													<step.icon />
												) : (
													<span className={s.step__index}>{index + 1}</span>
												)}
											</div>
											<div className={s.step__label}>{step.label}</div>
											{(tepeModal === 'applications' || lockRecipients) &&
												step.id === 'students' && (
													<div className={s.step__label}>
														{selectedStudentsLocal?.[0]?.name || 'Student'}
													</div>
												)}
										</li>
									)
								})}
							</ul>
						</aside>

						<div className={s.body}>
							<main className={s.main}>
								<div className={s.mainBody}>
									{activeStep.id === 'students' && (
										<StepRecipients
											selectedStudents={selectedStudentsLocal}
											savedLists={savedListsFromHook}
											activeGrade={localActiveGrade}
											onSelectList={handleSelectList}
										/>
									)}

									{tepeModal === 'applications' ? (
										<div
											style={{
												display:
													activeStep.id === 'assignmentType' ? 'block' : 'none',
											}}
											aria-hidden={activeStep.id !== 'assignmentType'}
										>
											<StepTypeApplications
												assignmentType={activityType}
												onSelect={selectActivityType}
												missingItems={stepMissing}
												uploadedItems={stepUploaded}
												onDocumentTargetsChange={onDocumentTargetsChange}
												essayPrompt={essayPrompt}
												onEssayPromptChange={setEssayPrompt}
												essayWordLimit={essayWordLimit}
												onEssayWordLimitChange={setEssayWordLimit}
												essayMode={essayMode}
												onEssayModeChange={setEssayMode}
												lorRecommenderName={lorRecommenderName}
												onLorRecommenderNameChange={setLorRecommenderName}
												lorProfessorDesignation={lorProfessorDesignation}
												onLorProfessorDesignationChange={
													setLorProfessorDesignation
												}
												applicationUniversityName={initialUniversityName}
												hasApplicationTargetContext={Boolean(
													initialApplicationTargetId,
												)}
												essayUniversityOptions={essayUniversityOptions}
												selectedEssayUniversityTargetId={
													selectedEssayUniversityTargetId
												}
												onEssayUniversityChange={
													setSelectedEssayUniversityTargetId
												}
												isEssayUniversityLoading={isEssayUniversityLoading}
												documentsLoading={documentsLoading}
												lockedSelection={stepLocked}
												resetKey={applicationModalInstanceKey}
											/>
										</div>
									) : (
										activeStep.id === 'assignmentType' && (
											<StepTypeActivity
												activityType={activityType}
												onSelect={selectActivityType}
											/>
										)
									)}

									{activeStep.id === 'timeline' && (
										<StepTimeline
											onValidityChange={setIsTimelineValid}
											onTimelineDataChange={setTimelineDataHandler}
											initialData={timelineData}
										/>
									)}

									{activeStep.id === 'description' && (
										<StepDescription
											value={description}
											onChange={setDescription}
										/>
									)}

									{activeStep.id === 'review' && (
										<StepPreview
											preview={preview}
											setField={setPreviewField}
											onOpenTab={goToStep}
											isEditMode={editMode}
											toggleFlag={togglePreviewFlag}
											activityTitle={selectedActivityTitle}
											activityText={activityText}
											setActivityText={setActivityText}
											isEditing={isEditing}
											startEdit={startEdit}
											acceptEdit={acceptEdit}
											isSaved={isSaved}
											askAiText={askAiText}
											setAskAiText={setAskAiText}
											onAskAi={onAskAi}
											students={selectedStudentsLocal}
											listLabel={listLabel}
											timelineData={timelineData}
											customMessage={customMessage}
											onCustomMessageChange={setCustomMessage}
										/>
									)}
								</div>
							</main>

							<footer className={s.footer}>
								<button
									onClick={handleBackClick}
									className={s.back}
									disabled={isFirstStep || isSubmitting}
								>
									<span className={s.back__icon}>
										<ArrowShortIcon className={s.footerArrow} />
									</span>
									<span className={s.back__text}>Back</span>
								</button>
								<div className={s.stepCounter}>
									Step {currentStepNumber} of {totalSteps}
								</div>

								<Button
									onClick={handleNextClick}
									variant="primary"
									className={s.footerButton}
									disabled={isPrimaryButtonDisabled}
									size={isMobile ? 'sm' : 'md'}
									leftIcon={
										isLastStep ? (
											<SendIcon className={s.footerSendIcon} />
										) : null
									}
									rightIcon={
										isLastStep ? null : (
											<ArrowShortIcon
												className={`${s.footerArrow} ${s.footerArrowRight}`}
											/>
										)
									}
								>
									<span>{buttonText}</span>
								</Button>
							</footer>
						</div>
					</div>
				</div>
			</div>
		) : null

		return (
			<>
				{isOpen && createPortal(content, document.body)}
				<Toast
					isVisible={showToast}
					message={toastMessage}
					onClose={closeToast}
				/>
			</>
		)
	},
)

export default AssignActivityModal
